import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import internalApi from "../../api/internalAxios";

const STATUS_OPTIONS = ["trial", "active", "past_due", "canceled"];

const InternalDashboard = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [featureFlags, setFeatureFlags] = useState({ map_module_enabled: true });
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [flagsSaving, setFlagsSaving] = useState(false);

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [editForm, setEditForm] = useState({
    plan_code: "",
    billing_status: "trial",
    trial_ends_at: "",
  });

  const userRaw = localStorage.getItem("internal_user");
  const internalUser = userRaw ? JSON.parse(userRaw) : null;

  const offset = (page - 1) * pageSize;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await internalApi.get("/internal/businesses", {
        params: {
          q,
          billing_status: status,
          limit: pageSize,
          offset,
        },
      });
      setItems(res.data?.items || []);
      setTotal(Number(res.data?.total || 0));
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo cargar el backoffice.");
    } finally {
      setLoading(false);
    }
  };

  const loadFeatureFlags = async () => {
    try {
      setFlagsLoading(true);
      const res = await internalApi.get("/feature-flags/internal");
      const mapEnabled = Boolean(res.data?.flags?.map_module_enabled);
      setFeatureFlags({ map_module_enabled: mapEnabled });
    } catch (err) {
      setFeatureFlags({ map_module_enabled: true });
      setError((prev) => prev || "No se pudo cargar configuracion global.");
    } finally {
      setFlagsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [q, status, page, pageSize]);

  useEffect(() => {
    loadFeatureFlags();
  }, []);

  const saveMapModuleFlag = async (enabled) => {
    try {
      setFlagsSaving(true);
      await internalApi.patch("/feature-flags/internal", {
        map_module_enabled: Boolean(enabled),
      });
      setFeatureFlags({ map_module_enabled: Boolean(enabled) });
      setOkMsg(
        Boolean(enabled)
          ? "Modulo mapa activado globalmente."
          : "Modulo mapa desactivado globalmente."
      );
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo actualizar modulo mapa.");
    } finally {
      setFlagsSaving(false);
    }
  };

  const metrics = useMemo(() => {
    const map = { trial: 0, active: 0, past_due: 0, canceled: 0 };
    items.forEach((i) => {
      if (map[i.billing_status] !== undefined) map[i.billing_status] += 1;
    });
    return map;
  }, [items]);

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({
      plan_code: row.plan_code || "starter_10",
      billing_status: row.billing_status || "trial",
      trial_ends_at: row.trial_ends_at ? row.trial_ends_at.slice(0, 10) : "",
    });
    setOkMsg("");
    setError("");
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      await internalApi.patch(`/internal/businesses/${editing.id}`, editForm);
      setOkMsg("Cuenta actualizada.");
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo actualizar la cuenta.");
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!editing) return;
    try {
      setDispatching(true);
      await internalApi.post("/internal/emails/dispatch", {
        event_type: "billing_status_" + (editForm.billing_status || "trial"),
        business_id: editing.id,
        data: {
          plan_code: editForm.plan_code,
          billing_status: editForm.billing_status,
          trial_ends_at: editForm.trial_ends_at || null,
        },
      });
      setOkMsg("Evento de email enviado a n8n.");
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo enviar evento a n8n.");
    } finally {
      setDispatching(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("internal_token");
    localStorage.removeItem("internal_user");
    navigate("/internal/login");
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-0">Backoffice interno</h3>
          <small className="text-muted">
            {internalUser?.email || "admin"} - gestiÃ³n de cuentas
          </small>
        </div>
        <Button variant="outline-danger" size="sm" onClick={logout}>
          Cerrar sesiÃ³n
        </Button>
      </div>

      {error ? (
        <Card className="mb-3 border-danger">
          <Card.Body className="text-danger">{error}</Card.Body>
        </Card>
      ) : null}
      {okMsg ? (
        <Card className="mb-3 border-success">
          <Card.Body className="text-success">{okMsg}</Card.Body>
        </Card>
      ) : null}

      <Row className="g-2 mb-3">
        <Col md={3}>
          <Card className="shadow-sm"><Card.Body>Trial: <strong>{metrics.trial}</strong></Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm"><Card.Body>Active: <strong>{metrics.active}</strong></Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm"><Card.Body>Past due: <strong>{metrics.past_due}</strong></Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm"><Card.Body>Canceled: <strong>{metrics.canceled}</strong></Card.Body></Card>
        </Col>
      </Row>

      <Card className="shadow-sm mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h6 className="mb-1">Modulos globales</h6>
              <small className="text-muted">
                Control rapido para apagar el mapa en Home/Landing ante incidentes.
              </small>
            </div>
            <Form.Check
              type="switch"
              id="map-module-switch"
              label={featureFlags.map_module_enabled ? "Mapa activo" : "Mapa desactivado"}
              checked={Boolean(featureFlags.map_module_enabled)}
              disabled={flagsLoading || flagsSaving}
              onChange={(e) => saveMapModuleFlag(e.target.checked)}
            />
          </div>
        </Card.Body>
      </Card>

      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col md={5}>
              <Form.Control
                placeholder="Buscar por nombre o slug"
                value={q}
                onChange={(e) => {
                  setPage(1);
                  setQ(e.target.value);
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Select
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(e.target.value);
                }}
              >
                <option value="all">Todos</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button variant="outline-secondary" className="w-100" onClick={load} disabled={loading}>
                Actualizar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>ID</th>
            <th>Negocio</th>
            <th>Plan</th>
            <th>Status</th>
            <th>Trial</th>
            <th>Email Admin</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {!loading &&
            items.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>
                  <div className="fw-semibold">{row.name}</div>
                  <small className="text-muted">{row.slug}</small>
                </td>
                <td>{row.plan_code || "-"}</td>
                <td>
                  <Badge bg={row.billing_status === "active" ? "success" : "secondary"}>
                    {row.billing_status || "-"}
                  </Badge>
                </td>
                <td>
                  {row.trial_days_left ?? 0} dÃ­as
                  <br />
                  <small className="text-muted">
                    {row.trial_ends_at ? new Date(row.trial_ends_at).toLocaleDateString() : "-"}
                  </small>
                </td>
                <td>{row.admin_email || "-"}</td>
                <td>
                  <Button size="sm" variant="outline-primary" onClick={() => openEdit(row)}>
                    Editar cuenta
                  </Button>
                </td>
              </tr>
            ))}
          {!loading && items.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center text-muted py-3">
                Sin resultados.
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center">
        <small className="text-muted">
          {total === 0 ? 0 : offset + 1}-{Math.min(offset + pageSize, total)} de {total}
        </small>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </Button>
        </div>
      </div>

      <Modal show={!!editing} onHide={() => setEditing(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Editar cuenta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Plan code</Form.Label>
            <Form.Control
              value={editForm.plan_code}
              onChange={(e) => setEditForm((p) => ({ ...p, plan_code: e.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Billing status</Form.Label>
            <Form.Select
              value={editForm.billing_status}
              onChange={(e) => setEditForm((p) => ({ ...p, billing_status: e.target.value }))}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Trial ends at</Form.Label>
            <Form.Control
              type="date"
              value={editForm.trial_ends_at}
              onChange={(e) => setEditForm((p) => ({ ...p, trial_ends_at: e.target.value }))}
            />
          </Form.Group>

          <div className="d-flex gap-2">
            <Button variant="primary" onClick={saveEdit} disabled={saving}>
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
            <Button variant="outline-secondary" onClick={sendTestEmail} disabled={dispatching}>
              {dispatching ? "Enviando..." : "Enviar evento email"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default InternalDashboard;
